import io from 'socket.io-client';

class WebSocketService {
  constructor() {
    this.socket = null;
    this.storeId = null;
    this.tableCode = null;
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 10;
    this.reconnectDelay = 1000; // 1초부터 시작
    this.heartbeatInterval = null;
    this.userCountCallback = null;
    this.connectionStateCallback = null;
    this.memberCartCallback = null;
    this.orderCompletedCallback = null;
    this.cartUpdatedCallbacks = []; // 여러 콜백을 지원하기 위해 배열로 변경
    this.cartRequestCallback = null;
    this.cartResponseCallback = null;
    this.noticeUpdatedCallback = null;
    this.menuUpdatedCallback = null;
    this.bannerUpdatedCallback = null;
    this.storeUpdatedCallback = null;
    this.wowposOrderCallback = null;
    this.wowposTableClearedCallback = null;
    this.wowposCallCallback = null;
    this.wowposReviewCallback = null;
    this.wowposNotifyRemoveCallback = null;
  }

  connect(storeId, tableCode = null, options = {}) {
    // 이미 연결되어 있으면 먼저 끊기
    if (this.socket && this.isConnected) {
      this.disconnect();
    }

    this.storeId = storeId;
    this.tableCode = tableCode;

    // WebSocket 서버 URL 결정
    const wsUrl = process.env.NODE_ENV === 'production'
      ? ''  // 프로덕션: 빈 문자열 (현재 도메인 사용)
      : 'http://localhost:5000'; // 개발: 백엔드 서버

    console.log('Connecting to WebSocket server:', wsUrl || window.location.origin);
    console.log('Table Code:', tableCode);

    // Socket.IO 연결
    this.socket = io(wsUrl || window.location.origin, {
      path: '/socket.io/',
      transports: ['polling', 'websocket'], // polling을 먼저 시도
      secure: window.location.protocol === 'https:', // HTTPS면 WSS 사용
      reconnection: true,
      reconnectionAttempts: this.maxReconnectAttempts,
      reconnectionDelay: this.reconnectDelay,
      reconnectionDelayMax: 10000,
      timeout: 20000,
      ...options
    });

    this.setupEventListeners();

    return this.socket;
  }

  setupEventListeners() {
    // 연결 성공
    this.socket.on('connect', () => {
      console.log('WebSocket connected');
      this.isConnected = true;
      this.reconnectAttempts = 0;
      this.startHeartbeat();

      if (this.connectionStateCallback) {
        this.connectionStateCallback(true);
      }

      // 연결 후 매장 입장 (isConnected = true 설정 후 호출)
      if (this.storeId) {
        console.log('WebSocket connected, joining store:', this.storeId, 'table:', this.tableCode);
        this.socket.emit('join-store', { storeId: this.storeId, tableCode: this.tableCode });
      }
    });

    // 연결 끊김
    this.socket.on('disconnect', (reason) => {
      console.log('WebSocket disconnected:', reason);
      this.isConnected = false;
      this.stopHeartbeat();

      if (this.connectionStateCallback) {
        this.connectionStateCallback(false);
      }
    });

    // 재연결 시도
    this.socket.on('reconnect_attempt', (attemptNumber) => {
      console.log(`Reconnection attempt ${attemptNumber}`);
      this.reconnectAttempts = attemptNumber;
    });

    // 재연결 성공
    this.socket.on('reconnect', (attemptNumber) => {
      console.log(`Reconnected after ${attemptNumber} attempts`);

      // 재연결 후 자동으로 매장 재입장
      if (this.storeId) {
        this.joinStore(this.storeId, this.tableCode);
      }
    });

    // 재연결 실패
    this.socket.on('reconnect_failed', () => {
      console.error('Failed to reconnect after maximum attempts');

      if (this.connectionStateCallback) {
        this.connectionStateCallback(false, 'failed');
      }
    });

    // 에러 처리
    this.socket.on('error', (error) => {
      console.error('WebSocket error:', error);
    });

    // 연결 에러
    this.socket.on('connect_error', (error) => {
      console.error('Connection error:', error.message);

      // 특정 에러에 대한 처리
      if (error.type === 'TransportError') {
        console.log('Transport error, trying polling');
        this.socket.io.opts.transports = ['polling'];
      }
    });

    // 사용자 수 업데이트
    this.socket.on('user-count-update', (data) => {
      console.log('User count updated:', data.count);

      if (this.userCountCallback) {
        this.userCountCallback(data.count);
      }
    });

    // 하트비트 응답
    this.socket.on('heartbeat-ack', () => {
      // 하트비트 응답 받음 (연결 정상)
    });

    // 서버 에러 메시지
    this.socket.on('error', (data) => {
      console.error('Server error:', data.message);
    });

    // 다른 멤버가 장바구니에 추가했을 때
    this.socket.on('member-added-to-cart', () => {
      console.log('Member added to cart');

      if (this.memberCartCallback) {
        this.memberCartCallback();
      }
    });

    // 다른 멤버가 주문을 완료했을 때
    this.socket.on('member-order-completed', () => {
      console.log('Member completed order');

      if (this.orderCompletedCallback) {
        this.orderCompletedCallback();
      }
    });

    // 서버에서 장바구니 상태 수신 (하이브리드)
    this.socket.on('cart-state', (data) => {
      console.log('Cart state received from server:', data.cart);
      console.log('등록된 콜백 수:', this.cartUpdatedCallbacks.length);

      // 모든 등록된 콜백 실행
      this.cartUpdatedCallbacks.forEach(callback => {
        if (callback) {
          callback(data.cart);
        }
      });
    });

    // 기존 cart-updated 이벤트도 호환성을 위해 유지
    this.socket.on('cart-updated', (data) => {
      console.log('Cart updated (legacy):', data);

      // 모든 등록된 콜백 실행
      this.cartUpdatedCallbacks.forEach(callback => {
        if (callback) {
          callback(data.cartData);
        }
      });
    });

    // 다른 사용자가 장바구니를 요청했을 때
    this.socket.on('cart-request-received', (data) => {
      console.log('Cart request received from:', data.requesterId);

      if (this.cartRequestCallback) {
        this.cartRequestCallback(data.requesterId);
      }
    });

    // 장바구니 요청에 대한 응답 받음
    this.socket.on('cart-response', (data) => {
      console.log('Cart response received:', data);

      if (this.cartResponseCallback) {
        this.cartResponseCallback(data.cartData);
      }
    });

    // 공지사항 업데이트 받음 (매장 전체)
    this.socket.on('notice-updated', (data) => {
      console.log('[WebSocket] notice-updated 수신:', data);

      if (this.noticeUpdatedCallback) {
        this.noticeUpdatedCallback(data);
      }
    });

    // 메뉴 업데이트 받음
    this.socket.on('menu-updated', (data) => {
      console.log('[WebSocket] menu-updated 수신:', data);
      if (this.menuUpdatedCallback) {
        this.menuUpdatedCallback(data);
      }
    });

    // 와우포스 주문 도착 — 관리자 주문내역 총괄 화면 실시간 반영
    this.socket.on('wowpos-order', (data) => {
      if (this.wowposOrderCallback) {
        this.wowposOrderCallback(data);
      }
    });

    // 와우포스 테이블 초기화 — 관리자 화면 실시간 반영
    this.socket.on('wowpos-table-cleared', (data) => {
      if (this.wowposTableClearedCallback) {
        this.wowposTableClearedCallback(data);
      }
    });

    // 와우포스 직원호출 — 관리자 앱 미러 팝업
    this.socket.on('wowpos-call', (data) => {
      if (this.wowposCallCallback) {
        this.wowposCallCallback(data);
      }
    });

    // 와우포스 리뷰 작성완료 — 관리자 앱 미러 팝업
    this.socket.on('wowpos-review', (data) => {
      if (this.wowposReviewCallback) {
        this.wowposReviewCallback(data);
      }
    });

    // 와우포스 팝업 dismiss 동기화 — PC/다른 앱에서 지운 팝업 제거
    this.socket.on('wowpos-notify-remove', (data) => {
      if (this.wowposNotifyRemoveCallback) {
        this.wowposNotifyRemoveCallback(data);
      }
    });

    // 배너 업데이트 받음
    this.socket.on('banner-updated', (data) => {
      console.log('[WebSocket] banner-updated 수신:', data);
      if (this.bannerUpdatedCallback) {
        this.bannerUpdatedCallback(data);
      }
    });

    // 매장 정보 업데이트 받음
    this.socket.on('store-updated', (data) => {
      console.log('[WebSocket] store-updated 수신:', data);
      if (this.storeUpdatedCallback) {
        this.storeUpdatedCallback(data);
      }
    });
  }

  joinStore(storeId, tableCode = null) {
    if (!this.socket || !this.isConnected) {
      console.warn('Cannot join store: not connected');
      return;
    }

    this.storeId = storeId;
    this.tableCode = tableCode;
    this.socket.emit('join-store', { storeId, tableCode });
    console.log('Joined store:', storeId, 'table:', tableCode);
  }

  leaveStore() {
    if (!this.socket || !this.isConnected) {
      return;
    }

    this.socket.emit('leave-store');
    console.log('Left store');
  }

  disconnect() {
    this.stopHeartbeat();

    if (this.socket) {
      this.leaveStore();
      this.socket.disconnect();
      this.socket = null;
    }

    this.isConnected = false;
    this.storeId = null;
    this.tableCode = null;
    console.log('WebSocket disconnected manually');
  }

  // 하트비트 (연결 상태 유지)
  startHeartbeat() {
    this.stopHeartbeat();

    this.heartbeatInterval = setInterval(() => {
      if (this.socket && this.isConnected) {
        this.socket.emit('heartbeat');
      }
    }, 30000); // 30초마다
  }

  stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  // 콜백 등록
  onUserCountUpdate(callback) {
    this.userCountCallback = callback;
  }

  onConnectionStateChange(callback) {
    this.connectionStateCallback = callback;
  }

  onMemberAddedToCart(callback) {
    this.memberCartCallback = callback;
  }

  onOrderCompleted(callback) {
    this.orderCompletedCallback = callback;
  }

  onCartUpdated(callback) {
    if (callback === null) {
      // null이 전달되면 모든 콜백 제거
      console.log('[WebSocket] onCartUpdated: 모든 콜백 제거');
      this.cartUpdatedCallbacks = [];
    } else if (typeof callback === 'function') {
      // 콜백이 이미 등록되어 있지 않으면 추가
      if (!this.cartUpdatedCallbacks.includes(callback)) {
        this.cartUpdatedCallbacks.push(callback);
        console.log('[WebSocket] onCartUpdated: 새 콜백 추가 완료, 전체 콜백 수:', this.cartUpdatedCallbacks.length);
      } else {
        console.log('[WebSocket] onCartUpdated: 이미 등록된 콜백');
      }
    }
  }

  offCartUpdated(callback) {
    // 특정 콜백만 제거
    if (callback && typeof callback === 'function') {
      const index = this.cartUpdatedCallbacks.indexOf(callback);
      if (index > -1) {
        this.cartUpdatedCallbacks.splice(index, 1);
        console.log('[WebSocket] offCartUpdated: 콜백 제거 완료, 남은 콜백 수:', this.cartUpdatedCallbacks.length);
      } else {
        console.log('[WebSocket] offCartUpdated: 제거할 콜백을 찾을 수 없음');
      }
    }
  }

  onCartRequestReceived(callback) {
    this.cartRequestCallback = callback;
  }

  onCartResponse(callback) {
    this.cartResponseCallback = callback;
  }

  onNoticeUpdated(callback) {
    this.noticeUpdatedCallback = callback;
  }

  onMenuUpdated(callback) {
    this.menuUpdatedCallback = callback;
  }

  onBannerUpdated(callback) {
    this.bannerUpdatedCallback = callback;
  }

  onStoreUpdated(callback) {
    this.storeUpdatedCallback = callback;
  }

  onWowposOrder(callback) {
    this.wowposOrderCallback = callback;
  }

  onWowposTableCleared(callback) {
    this.wowposTableClearedCallback = callback;
  }

  onWowposCall(callback) {
    this.wowposCallCallback = callback;
  }

  onWowposReview(callback) {
    this.wowposReviewCallback = callback;
  }

  onWowposNotifyRemove(callback) {
    this.wowposNotifyRemoveCallback = callback;
  }

  // 하이브리드 방식: 장바구니에 아이템 추가
  addCartItem(item) {
    if (!this.socket || !this.isConnected) {
      console.warn('Cannot add cart item: not connected');
      return Promise.reject(new Error('Not connected'));
    }

    console.log('Sending add-cart-item to server:', item);
    this.socket.emit('add-cart-item', item);
  }

  // 장바구니에서 아이템 제거
  removeCartItem(itemId) {
    if (!this.socket || !this.isConnected) {
      console.warn('Cannot remove cart item: not connected');
      return;
    }

    // console.log('Sending remove-cart-item to server:', itemId);
    this.socket.emit('remove-cart-item', itemId);
  }

  // 장바구니 수량 변경
  updateCartQuantity(itemId, quantity) {
    if (!this.socket || !this.isConnected) {
      console.warn('Cannot update quantity: not connected');
      return;
    }

    console.log('[WebSocket] update-cart-quantity 전송:', { itemId, quantity });
    this.socket.emit('update-cart-quantity', { itemId, quantity });
  }

  // 기존 메서드 (호환성)
  emitCartItemAdded() {
    console.warn('emitCartItemAdded is deprecated. Use addCartItem instead.');
  }

  // 주문 완료 알림 전송
  emitOrderCompleted() {
    if (!this.socket || !this.isConnected) {
      console.warn('Cannot emit order event: not connected');
      return;
    }

    this.socket.emit('order-completed');
  }

  // 장바구니 동기화
  syncCart(cartData) {
    if (!this.socket || !this.isConnected) {
      console.warn('Cannot sync cart: not connected');
      return;
    }

    this.socket.emit('cart-sync', {
      cartData: cartData
    });
  }

  // 장바구니 요청 (새로 접속한 사용자가 호출)
  requestCart() {
    if (!this.socket || !this.isConnected) {
      console.warn('Cannot request cart: not connected');
      return;
    }

    this.socket.emit('request-cart');
  }

  // 장바구니 응답 (기존 사용자가 호출)
  sendCartResponse(requesterId, cartData) {
    if (!this.socket || !this.isConnected) {
      console.warn('Cannot send cart response: not connected');
      return;
    }

    this.socket.emit('send-cart-response', {
      requesterId: requesterId,
      cartData: cartData
    });
  }

  // 현재 상태 확인
  isSocketConnected() {
    return this.socket && this.isConnected;
  }

  // 소켓이 websocket 으로 업그레이드된 "살아있는" 상태인지.
  // polling 잔류(핸드셰이크만)면 콜드 HTTP 와 같은 stall 위험 → 이 경우 소켓 우선 안 하고 HTTP 폴백 권장.
  isWebsocketUpgraded() {
    try {
      return !!(this.socket && this.isConnected &&
        this.socket.io && this.socket.io.engine &&
        this.socket.io.engine.transport &&
        this.socket.io.engine.transport.name === 'websocket');
    } catch (_) {
      return false;
    }
  }

  // emit + ack + 타임아웃. 소켓 미연결/무응답이면 reject → 호출측이 HTTP 로 폴백한다.
  // (손님 Safari 의 죽은 keep-alive HTTP 소켓 stall 을 우회하기 위한 주문/조회 전송용)
  emitWithAck(event, data, { timeoutMs = 2000 } = {}) {
    return new Promise((resolve, reject) => {
      if (!this.socket || !this.isConnected) {
        reject(new Error('socket not connected'));
        return;
      }
      let done = false;
      const timer = setTimeout(() => {
        if (done) return;
        done = true;
        reject(new Error('ack timeout'));
      }, timeoutMs);
      try {
        this.socket.emit(event, data, (resp) => {
          if (done) return;
          done = true;
          clearTimeout(timer);
          resolve(resp);
        });
      } catch (err) {
        if (done) return;
        done = true;
        clearTimeout(timer);
        reject(err);
      }
    });
  }

  getConnectionStatus() {
    if (!this.socket) return 'disconnected';
    if (this.isConnected) return 'connected';
    if (this.reconnectAttempts > 0) return 'reconnecting';
    return 'connecting';
  }
}

// 싱글톤 인스턴스
const wsService = new WebSocketService();

// 개발 환경에서 디버깅용
if (process.env.NODE_ENV === 'development') {
  window.wsService = wsService;
}

export default wsService;